import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Footprints, Hand } from "lucide-react";
import { api, playState } from "../../lib/api";
import { GreatBaobab } from "../../components/game/GreatBaobab";
import { PhoneFrame } from "../../components/game/PhoneFrame";
import { StopButton } from "../../components/game/GameHud";
import { Overlay } from "../../components/game/VillageOverlays";
import { BaoRig } from "../../game/bao/BaoRig";
import { useBaoModel } from "../../components/three/useBaoModel";
import { Bao3DActor } from "../../components/three/Bao3DActor";
import { sample } from "../../game/bao/rig";
import { DIFFICULTY, LEVELS, adapt, emptyMetrics, poseFor, runPayload, sideSequence, summarise, targetFor } from "../../engine/StepEngine";
import { Trail } from "../../components/game/savanna/Trail";
import { FootPad } from "../../components/game/savanna/FootPad";
import { FeedbackPanel } from "../../components/game/savanna/FeedbackPanel";
import { GoalBar, MoveBriefing, Portrait, SavannaComplete, SavannaIntro, SeedBurst } from "../../components/game/savanna/SavannaOverlays";
import { NayaGreeting } from "../../components/game/NayaGreeting";

const CHEERS = ["Un pas après l'autre, tu es sur la bonne voie !", "Magnifique ! La savane s'ouvre devant nous.", "Bravo, quel beau contrôle !", "Super ! Les animaux nous montrent le chemin."];
const MSG = { reached: "Parfait, tu y es ! Relâche tout doucement.", hold: "Tiens bon… respire calmement.", miss: "Presque ! Encore un petit peu, tout doucement.", fast: "Plus lentement, en douceur, c'est parfait." };
const SPEAKER = { bao: "Bao", naya: "Mama Naya" };

const useWalkClock = (on) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!on) { setT(0); return; }
    let raf, t0 = performance.now();
    const loop = (now) => { setT((now - t0) / 1000); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [on]);
  return t;
};

export default function SavannaLevel({ levelKey = "savanna" }) {
  const nav = useNavigate();
  const LV = LEVELS[levelKey], MOVES = LV.moves, arm = LV.limb === "arm", speaker = LV.speaker || "bao", CH = LV.cheers || CHEERS;
  const baoModel = useBaoModel();
  const { sessionId, patientId } = playState.get();
  const [ctx, setCtx] = useState({ pid: patientId, affected: "right", share: 0.6, baseSeeds: 0, goals: {} });
  const [sent, setSent] = useState(null);
  const [diff, setDiff] = useState("moyen");
  const [phase, setPhase] = useState("naya");
  const [mi, setMi] = useState(0);
  const [rep, setRep] = useState(0);
  const [amount, setAmount] = useState(0);
  const [hold, setHold] = useState(0);
  const [target, setTarget] = useState(DIFFICULTY.moyen.target);
  const [seeds, setSeeds] = useState(0);
  const [burst, setBurst] = useState(false);
  const [say, setSay] = useState(CH[0]);
  const [stats, setStats] = useState(summarise(emptyMetrics()));
  const m = useRef(emptyMetrics());
  const cur = useRef({ peak: 0, fast: false, firstTry: true, start: 0, moved: false });
  const amountRef = useRef(0);
  const relaxRaf = useRef(null);
  const cfg = DIFFICULTY[diff];
  const seq = useMemo(() => sideSequence(MOVES.length * cfg.reps, ctx.affected, ctx.share), [MOVES.length, cfg.reps, ctx.affected, ctx.share]);
  const sideOf = (i) => seq[mi * cfg.reps + i] || "right";
  const move = MOVES[mi], side = sideOf(rep), tgt = targetFor(move, target);

  useEffect(() => {
    (async () => {
      const pid = patientId || (await api.get("/patients")).data.find((p) => p.is_demo)?.id;
      if (!pid) return;
      const [p, g, sess] = await Promise.all([api.get(`/patients/${pid}`), api.get(`/patients/${pid}/game-progress`), sessionId ? api.get(`/sessions/${sessionId}`) : Promise.resolve(null)]);
      setCtx({ pid, affected: p.data.clinical_profile.affected_side, share: sess?.data.config.affected_leg_share ?? 0.6, baseSeeds: g.data.seeds, goals: arm ? {} : sess?.data.config.leg_goals || {} });
    })().catch(() => {});
  }, [patientId, sessionId, arm]);

  const count = (ok) => {
    const sd = m.current.sides[side], mv = (m.current.moves[move.key] ||= { reps: cfg.reps, attempts: 0, successes: 0 });
    sd.attempts += 1; mv.attempts += 1;
    if (ok) { sd.successes += 1; sd.peaks.push(cur.current.peak); mv.successes += 1; }
  };
  const walking = phase === "walking";
  const t = useWalkClock(walking);

  const setAmt = (a) => { amountRef.current = a; setAmount(a); };
  const refresh = () => setStats(summarise(m.current));
  const beginRep = () => { cur.current = { peak: 0, fast: false, firstTry: true, start: performance.now(), moved: false }; setAmt(0); setHold(0); };

  const relax = useCallback(() => {
    cancelAnimationFrame(relaxRaf.current);
    const step = () => { const a = Math.max(0, amountRef.current - 0.04); amountRef.current = a; setAmount(a); if (a > 0) relaxRaf.current = requestAnimationFrame(step); };
    relaxRaf.current = requestAnimationFrame(step);
  }, []);

  const succeed = useCallback(() => {
    const c = cur.current;
    m.current.successes += 1; m.current.peaks.push(c.peak); m.current.ratios.push(c.peak / tgt); count(true);
    if (c.fast) m.current.fast += 1;
    setTarget((x) => adapt(x, c.firstTry));
    refresh();
    setSay(CH[(m.current.successes) % CH.length]);
    setPhase("walking");
    setTimeout(() => {
      const last = rep + 1 >= cfg.reps;
      if (!last) { setRep(rep + 1); beginRep(); setPhase("play"); return; }
      setSeeds((s) => s + 1); setBurst(true); setRep(rep + 1); setAmt(0);
      setTimeout(() => {
        setBurst(false);
        if (mi + 1 >= MOVES.length) {
          const body = runPayload(m.current, { sessionId, diff, affected: ctx.affected, share: ctx.share, seeds: MOVES.length, level: levelKey, goals: ctx.goals });
          localStorage.setItem(`baobab.${levelKey}.last`, JSON.stringify({ at: new Date().toISOString(), ...body }));
          setPhase("done");
          if (ctx.pid) api.post(`/patients/${ctx.pid}/savanna-runs`, body).then(() => setSent("ok")).catch(() => setSent("error"));
        } else { setMi(mi + 1); setRep(0); beginRep(); setPhase("brief"); }
      }, 1100);
    }, 1300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tgt, rep, cfg.reps, mi, diff, side, ctx, levelKey]);

  useEffect(() => {
    if (phase !== "play" || !move.hold) return;
    let raf, last = performance.now(), h = 0;
    const loop = (now) => {
      const dt = (now - last) / 1000; last = now;
      if (amountRef.current >= tgt) { h += dt; m.current.balance += dt; m.current.sides[side].balance += dt; setHold(h); }
      if (h >= cfg.hold * (move.holdMult || 1)) { cur.current.peak = Math.max(cur.current.peak, amountRef.current); m.current.attempts += 1; succeed(); relax(); return; }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase, move.hold, move.holdMult, tgt, cfg.hold, succeed, relax, side]);

  const onDrag = (a, speed) => {
    if (phase !== "play") return;
    cancelAnimationFrame(relaxRaf.current);
    const c = cur.current;
    if (!c.moved && a > 0.03) { c.moved = true; m.current.reactions.push((performance.now() - c.start) / 1000); }
    if (speed > 2.4 && a > 0.1) { c.fast = true; setSay(MSG.fast); } else if (a >= tgt) setSay(move.hold ? MSG.hold : MSG.reached);
    c.peak = Math.max(c.peak, a);
    setAmt(a);
  };

  const onRelease = () => {
    if (phase !== "play") return;
    const c = cur.current;
    relax();
    if (move.hold) { if (c.peak < tgt) { m.current.attempts += 1; count(false); c.firstTry = false; setTarget((x) => adapt(x, false)); setSay(MSG.miss); refresh(); } else setSay(LV.words.rest); return; }
    if (c.peak < 0.05) return;
    m.current.attempts += 1;
    if (c.peak >= tgt) return succeed();
    count(false); c.firstTry = false; c.peak = 0; setTarget((x) => adapt(x, false)); setSay(MSG.miss); refresh();
  };

  const start = () => { setSent(null); if (seeds) setCtx((c) => ({ ...c, baseSeeds: c.baseSeeds + seeds })); setTarget(cfg.target); m.current = emptyMetrics(); refresh(); setMi(0); setRep(0); setSeeds(0); setPhase("brief"); };
  const exit = () => nav(sessionId ? "/patient/map" : "/bao");
  const pose = walking ? sample("walk", t) : poseFor(move, side, amount);

  return (
    <PhoneFrame>
      <div className="absolute inset-0 overflow-hidden" data-testid={`${levelKey}-level`}>
        <img decoding="async" src={LV.bg} alt="" className={`absolute inset-0 h-full w-full scale-[1.04] object-cover ${walking ? "scene-step" : ""}`} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A2540]/35 via-transparent to-[#0A2540]/55" />
        {["play", "walking"].includes(phase) && <Trail reps={cfg.reps} current={rep} reached={amount >= tgt} sideOf={sideOf} obstacle={move.key === "obstacle"} slots={LV.slots} variant={LV.trail} />}
        <SeedBurst show={burst} />
        {burst && (
          <div className="pointer-events-none absolute inset-x-0 top-[24%] z-30 flex flex-col items-center" data-testid="baobab-revives">
            <div className="rounded-[2rem] bg-white/90 p-3 shadow-2xl backdrop-blur"><GreatBaobab seeds={ctx.baseSeeds + seeds} fresh={1} size={150} testId="great-baobab-burst" /></div>
            <p className="mt-2 rounded-full bg-[#0A2540] px-4 py-1.5 font-display text-sm font-bold text-[#FFE29A]">Le Grand Baobab reprend vie !</p>
          </div>
        )}

        <header className="safe-top absolute inset-x-0 top-0 z-30 space-y-2 px-3">
          <div className="flex items-center gap-2">
            <StopButton onConfirm={exit} />
            <div className="min-w-0 flex-1 rounded-2xl bg-[#0A2540]/85 px-3 py-1.5 text-white backdrop-blur-md" data-testid="savanna-level-badge">
              <p className="truncate text-[11px] font-extrabold tracking-[0.12em]">{LV.title}</p>
              <p className="truncate text-[11px] text-[#9FE7DE]">{LV.subtitle}</p>
            </div>
            <div className="flex h-14 items-center gap-1 rounded-2xl bg-white/90 pl-1 pr-2.5 shadow-md backdrop-blur" data-testid="savanna-hud-seeds">
              <GreatBaobab seeds={ctx.baseSeeds + seeds} size={44} testId="great-baobab-hud" /><span className="font-display text-lg font-extrabold text-[#0A2540]" data-testid="savanna-hud-seeds-count">{seeds}</span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 rounded-full bg-white/90 py-1 pl-1 pr-3 shadow-md backdrop-blur" data-testid="savanna-move-chip">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A2540]">{arm ? <Hand className="h-4 w-4 text-white" /> : <Footprints className="h-4 w-4 text-white" />}</span>
              <p className="text-xs font-extrabold leading-tight text-[#0A2540]">Mouvement {mi + 1}/{MOVES.length}<span className="block font-semibold text-[#0F7C7C]">{move.label}</span></p>
            </div>
            <span className="rounded-full bg-[#0A2540]/75 px-2.5 py-1 text-[10px] font-bold tracking-widest text-white" data-testid="simulated-data-label">DONNÉES SIMULÉES</span>
          </div>
          {["play", "walking"].includes(phase) && ctx.goals[side] > 0 && <GoalBar side={side} goal={ctx.goals[side]} value={stats.sides[side].amplitude_pct} />}
          {["play", "walking"].includes(phase) && (
            <div className="ml-auto flex max-w-[78%] items-center gap-2 rounded-2xl bg-white/90 p-2 shadow-lg backdrop-blur" data-testid="bao-speech">
              <Portrait who={speaker} size={44} />
              <p className="text-xs leading-snug text-[#0A2540]"><b>{SPEAKER[speaker]} :</b> « {say} »</p>
            </div>
          )}
        </header>

        <div className={`absolute bottom-[222px] z-10 h-[262px] ${arm ? "-left-6 w-[270px]" : "-left-2 w-[176px]"}`} data-testid="savanna-bao">
          {baoModel ? <Bao3DActor model={baoModel} pose={pose} view={walking ? "profile" : move.view} clip={walking ? "walk" : null} /> : <BaoRig pose={pose} view={walking ? "profile" : move.view} width="100%" />}
        </div>

        {["play", "walking"].includes(phase) && (
          <div className="safe-bottom absolute inset-x-0 bottom-0 z-20 space-y-2 px-2">
            <FeedbackPanel s={stats} done={rep} total={cfg.reps} unit={arm ? "Gestes" : "Pas"} />
            <div className="flex items-end justify-between gap-1">
              <FootPad side="left" active={phase === "play" && side === "left"} axis={move.axis} amount={side === "left" ? amount : 0} target={tgt} onDrag={onDrag} onRelease={onRelease} limb={LV.limb} />
              <div className="mb-1 min-w-0 flex-1 rounded-2xl bg-[#0A2540]/90 px-2 py-2 text-center text-white backdrop-blur-md" data-testid="next-step-card">
                <p className="font-display text-sm font-bold">{LV.words.next}</p>
                <p className="text-[11px] leading-snug text-[#D7F5F1]" data-testid="next-step-cue">{move.cue(side)}</p>
                {move.hold && <div className="mx-auto mt-1.5 h-2 w-full overflow-hidden rounded-full bg-white/20"><div className="h-full rounded-full bg-[#E0B33A] transition-[width]" style={{ width: `${Math.min(100, (hold / (cfg.hold * (move.holdMult || 1))) * 100)}%` }} data-testid="hold-progress" /></div>}
              </div>
              <FootPad side="right" active={phase === "play" && side === "right"} axis={move.axis} amount={side === "right" ? amount : 0} target={tgt} onDrag={onDrag} onRelease={onRelease} limb={LV.limb} />
            </div>
          </div>
        )}

        {phase === "naya" && <Overlay><NayaGreeting level={levelKey} onNext={() => setPhase("intro")} /></Overlay>}
        {phase === "intro" && <Overlay><SavannaIntro level={LV} diff={diff} setDiff={setDiff} onGo={start} affected={ctx.affected} share={ctx.share} goals={ctx.goals} /></Overlay>}
        {phase === "brief" && <Overlay><MoveBriefing level={LV} move={move} index={mi} total={MOVES.length} target={tgt} firstSide={sideOf(0)} onGo={() => { beginRep(); setSay(CH[0]); setPhase("play"); }} /></Overlay>}
        {phase === "done" && <Overlay><SavannaComplete level={LV} goals={ctx.goals} s={stats} seeds={seeds} totalSeeds={ctx.baseSeeds + seeds} sent={sent} affected={ctx.affected} mapLabel={sessionId ? "Retour à la carte" : "Retour au studio"} onMap={exit} pid={ctx.pid} onAlbum={() => nav("/patient/album")} onReplay={() => setPhase("intro")} /></Overlay>}
      </div>
    </PhoneFrame>
  );
}
